import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownWideNarrow, BrainCircuit, HelpCircle, Camera, ChevronDown, ChevronUp, Clock, Crosshair, Maximize2, Mic, MicOff, Minimize2, Plus, RefreshCw, ShieldCheck, Sparkles, Sunrise, Trash2, X } from 'lucide-react';
import { useStore, selectFatigued } from '../store/useStore';
import { dailyAffirmation as buildAffirmation, localDateKey } from '../engine/affirmation';
import { zoneProgress } from '../engine/progress';
import { sortTasks, TAG_COLORS, tagReport, tagStats } from '../engine/tags';
import { formatFullDate } from '../engine/dates';
import { todayFilter } from '../engine/today';
import { binDaysLeft, visibleBin } from '../engine/bin';
import { ZONES, ZONE_BY_ID, weekLabel, type Task, type ZoneId } from '../types';
import { TaskCard } from '../components/TaskCard';
import { ShareButton } from '../components/ShareButton';
import { PrintButton } from '../components/PrintButton';
import { ZoneIcon } from '../components/ZoneIcon';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useDictation } from '../hooks/useDictation';
import { QUICK_DICTATION_HINT } from '../engine/dictationText';
import { publicUrl } from '../native/native';

/**
 * Main workspace. Mobile: accordion stack (one zone open at a time).
 * Desktop: the classic 2×2 Eisenhower quadrant grid, all zones visible.
 */
export function MatrixScreen() {
  const tasks = useStore((s) => s.tasks);
  const setScreen = useStore((s) => s.setScreen);
  const openRollover = useStore((s) => s.openRollover);
  const quickAdd = useStore((s) => s.quickAdd);
  const setImportStrategy = useStore((s) => s.setImportStrategy);
  const openScanFromBoard = useStore((s) => s.openScanFromBoard);
  const importNotice = useStore((s) => s.importNotice);
  const clearImportNotice = useStore((s) => s.clearImportNotice);
  const providerNotice = useStore((s) => s.providerNotice);
  const clearProviderNotice = useStore((s) => s.clearProviderNotice);
  const mergeReview = useStore((s) => s.mergeReview);
  const resolveMergeReview = useStore((s) => s.resolveMergeReview);
  const tags = useStore((s) => s.tags);
  const hasTags = Object.keys(tags).length > 0;
  const activeTagId = useStore((s) => s.activeTagId);
  const setActiveTag = useStore((s) => s.setActiveTag);
  const sortMode = useStore((s) => s.sortMode);
  const setSortMode = useStore((s) => s.setSortMode);
  // A saved "Project / Role" sort on a board with no tags sorts by
  // priority, and the Sort menu says so (14 Oct 2026) — the screen and the
  // menu never disagree.
  const effectiveSort = !hasTags && sortMode === 'tag' ? 'priority' : sortMode;
  const bin = useStore((s) => s.bin);
  const restoreFromBin = useStore((s) => s.restoreFromBin);
  const purgeFromBin = useStore((s) => s.purgeFromBin);
  const [binOpen, setBinOpen] = useState(false);
  /** Two-tap confirm for "Delete forever" — id of the armed row, if any.
      This file's whole reason for existing is an accidental one-tap delete
      (engine/bin.ts header, 28 Jul incident); shipping a one-tap
      irreversible delete on top of the recovery feature would repeat it. */
  const [purgeArmedId, setPurgeArmedId] = useState<string | null>(null);
  const [reportCopied, setReportCopied] = useState(false);
  const renameTag = useStore((s) => s.renameTag);
  const deleteTag = useStore((s) => s.deleteTag);
  const setTagColor = useStore((s) => s.setTagColor);
  const [tagRenameOpen, setTagRenameOpen] = useState(false);
  const [tagNameDraft, setTagNameDraft] = useState('');
  const [tagDeleteArmed, setTagDeleteArmed] = useState(false);
  const focusMode = useStore((s) => s.focusMode);
  const toggleFocusMode = useStore((s) => s.toggleFocusMode);
  const todayLensOn = useStore((s) => s.todayLensOn);
  const setTodayLens = useStore((s) => s.setTodayLens);
  const privacyNudgeSeen = useStore((s) => s.privacyNudgeSeen);
  const markPrivacyNudgeSeen = useStore((s) => s.markPrivacyNudgeSeen);
  const openSettings = useStore((s) => s.openSettings);
  const archive = useStore((s) => s.archive);
  const weekHistory = useStore((s) => s.weekHistory);
  const affirmationOn = useStore((s) => s.dailyAffirmation);
  const affirmationDismissedOn = useStore((s) => s.affirmationDismissedOn);
  const affirmationCollapsedOn = useStore((s) => s.affirmationCollapsedOn);
  const fatigueCollapsed = useStore((s) => s.fatigueCollapsed);
  const toggleFatigueCollapsed = useStore((s) => s.toggleFatigueCollapsed);
  const dismissAffirmationToday = useStore((s) => s.dismissAffirmationToday);
  const toggleAffirmationCollapsed = useStore((s) => s.toggleAffirmationCollapsed);
  const setDailyAffirmationPref = useStore((s) => s.setDailyAffirmationPref);
  const isDesktop = useIsDesktop();

  const [quickText, setQuickText] = useState('');
  const [quickZone, setQuickZone] = useState<ZoneId>('zone2');
  const voice = useDictation(setQuickText);

  const allTasks = useMemo(() => Object.values(tasks), [tasks]);
  const fatigued = useMemo(() => selectFatigued(tasks), [tasks]);

  // Daily affirmation: data-grounded, generated on device, once per day.
  const todayKey = localDateKey();
  const affirmationText = useMemo(() => {
    const open = allTasks.filter((t) => !t.done);
    const last = weekHistory[weekHistory.length - 1];
    return buildAffirmation({
      completedThisWeek: archive.length,
      openCount: open.length,
      rolledOver: open.filter((t) => t.rolloverCount > 0).length,
      maxRollover: open.reduce((m, t) => Math.max(m, t.rolloverCount), 0),
      ...(last && {
        lastWeek: {
          completedCount: last.completedCount,
          rolledOverCount: last.rolledOverCount,
          zone2Share: last.distribution.zone2,
          zone4Share: last.distribution.zone4,
        },
      }),
      dateKey: todayKey,
    });
  }, [allTasks, archive.length, weekHistory, todayKey]);
  const showAffirmation = affirmationOn && affirmationDismissedOn !== todayKey;
  const affirmationCollapsed = affirmationCollapsedOn === todayKey;

  const { byZone, lensHidden, lensVisibleOpen } = useMemo(() => {
    const tagVisible = activeTagId
      ? allTasks.filter((t) => (t.tags ?? []).includes(activeTagId))
      : allTasks;
    // Today lens (V2 §1): same zones, same colors, same ordering — just
    // fewer cards. Picks arrive in V2-B; undefined until then.
    const lensed = todayLensOn
      ? todayFilter(tagVisible, undefined)
      : { visible: tagVisible, hiddenCount: 0 };
    const map: Record<ZoneId, Task[]> = { zone1: [], zone2: [], zone3: [], zone4: [] };
    for (const t of lensed.visible) map[t.zone].push(t);
    // Canonical order by default; user-chosen sort otherwise.
    for (const z of ZONES) map[z.id] = sortTasks(map[z.id], effectiveSort, tags);
    return {
      byZone: map,
      lensHidden: lensed.hiddenCount,
      lensVisibleOpen: lensed.visible.filter((t) => !t.done).length,
    };
    // todayKey in deps: the lens re-evaluates when the local day turns over.
  }, [allTasks, activeTagId, effectiveSort, tags, todayLensOn, todayKey]);

  const activeTag = activeTagId ? tags[activeTagId] : undefined;
  // Zone spotlight (V2 §5): one zone fills the workspace; others collapse
  // to thin colored rails. Pure UI, per-render state.
  const [spotlightZone, setSpotlightZone] = useState<ZoneId | null>(null);

  // Tag-rail custom slider (§4d + Chad 12 Aug): fractions of scrollWidth.
  const railRef = useRef<HTMLDivElement | null>(null);
  const [rail, setRail] = useState({ visible: false, thumb: 1, left: 0 });
  const updateRail = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth + 4) {
      setRail((r) => (r.visible ? { visible: false, thumb: 1, left: 0 } : r));
      return;
    }
    setRail({
      visible: true,
      thumb: clientWidth / scrollWidth,
      left: scrollLeft / scrollWidth,
    });
  }, []);
  useEffect(() => {
    updateRail();
    window.addEventListener('resize', updateRail);
    return () => window.removeEventListener('resize', updateRail);
  }, [updateRail, tags, activeTagId]);
  /** Tap or drag anywhere on the track scrolls the rail (pointer events
   *  cover mouse AND touch — this is the phone's slider). */
  const dragRail = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = railRef.current;
    const track = e.currentTarget;
    if (!el) return;
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const jump = (clientX: number) => {
      const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      el.scrollLeft = frac * el.scrollWidth - el.clientWidth / 2;
    };
    jump(e.clientX);
    const onMove = (ev: PointerEvent) => jump(ev.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // Status report: generated text opens in an EDITABLE preview first —
  // enterprise register demands the user can adjust wording before it
  // reaches a boss's inbox (Chad, 31 Jul). Edits are one-off; tasks are
  // never changed by editing the report.
  const [reportDraft, setReportDraft] = useState<string | null>(null);
  const openTagReport = () => {
    if (!activeTag) return;
    setReportDraft(tagReport(activeTag, allTasks, weekLabel()));
  };
  const copyReport = async () => {
    if (reportDraft == null) return;
    await navigator.clipboard.writeText(reportDraft);
    setReportCopied(true);
    setTimeout(() => setReportCopied(false), 2000);
  };
  const shareReport = async () => {
    if (reportDraft == null) return;
    if (navigator.share) {
      try {
        await navigator.share({ text: reportDraft });
        setReportDraft(null);
        return;
      } catch {
        /* user cancelled the OS sheet — keep the editor open */
        return;
      }
    }
    await copyReport();
  };

  // Mobile accordion: auto-open the first zone that has tasks (Zone 1 first).
  const firstNonEmpty = ZONES.find((z) => byZone[z.id].length > 0)?.id ?? 'zone1';
  const [openZone, setOpenZone] = useState<ZoneId | null>(firstNonEmpty);

  const submitQuickAdd = () => {
    if (!quickText.trim()) return;
    quickAdd(quickText, quickZone);
    setOpenZone(quickZone);
    setQuickText('');
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-4 px-4 py-5 lg:max-w-7xl lg:px-8 lg:py-8">
      {/* Daily affirmation: the user's own evidence, mirrored back — never
          generic praise. It stays reachable ALL DAY: the chevron collapses it
          to a slim strip (re-expandable), the X removes it for the day only,
          and the tiny opt-out link is the permanent off-switch. */}
      {showAffirmation &&
        (affirmationCollapsed ? (
          <button
            onClick={toggleAffirmationCollapsed}
            aria-label="Show today's note"
            aria-expanded={false}
            className="flex w-full items-center gap-2.5 rounded-2xl border border-brand-200 dark:border-brand-500/20 bg-gradient-to-r from-brand-50 dark:from-brand-900/15 to-amber-50 dark:to-amber-950/10 px-3.5 py-2 text-left transition-colors hover:from-brand-100 dark:hover:from-brand-900/25"
          >
            <Sunrise size={15} className="shrink-0 text-amber-500 dark:text-amber-400" />
            <span className="flex-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Today's note
            </span>
            <ChevronDown size={14} className="shrink-0 text-slate-500" />
          </button>
        ) : (
          <div className="flex items-start justify-between gap-3 rounded-2xl border border-brand-200 dark:border-brand-500/20 bg-gradient-to-r from-brand-50 dark:from-brand-900/15 to-amber-50 dark:to-amber-950/10 p-3.5">
            <div className="flex items-start gap-2.5">
              <Sunrise size={17} className="mt-0.5 shrink-0 text-amber-500 dark:text-amber-400" />
              <div>
                <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                  {affirmationText}
                </p>
                <button
                  onClick={() => setDailyAffirmationPref(false)}
                  className="mt-1.5 text-[10px] font-medium text-slate-400 dark:text-slate-500 underline-offset-2 transition-colors hover:text-slate-600 dark:hover:text-slate-300 hover:underline"
                >
                  Don't show these — you can re-enable in Settings
                </button>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                onClick={toggleAffirmationCollapsed}
                aria-label="Collapse today's note"
                aria-expanded={true}
                title="Collapse — reopen it anytime today"
                className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={dismissAffirmationToday}
                aria-label="Remove for today"
                title="Remove for today — back tomorrow"
                className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}

      {/* Uncertain-merge review: possible rewordings of tasks already on the
          board. We ask instead of guessing — importing a duplicate and
          silently swallowing a real task are both worse than one question. */}
      {mergeReview.length > 0 && (
        <div className="space-y-2.5 rounded-2xl border border-brand-200 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/10 p-3.5">
          <p className="text-xs font-bold text-slate-900 dark:text-white">
            Quick check — {mergeReview.length === 1 ? 'is this' : 'are these'} already on your
            board?
          </p>
          {mergeReview.map((entry, i) => (
            <div
              key={`${entry.item.text}-${i}`}
              className="space-y-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5"
            >
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                “{entry.item.text}”
              </p>
              {entry.kind === 'completed' ? (
                <>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    looks ticked off / crossed out in your notebook
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    <button
                      onClick={() => resolveMergeReview(i, 'done')}
                      className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-emerald-500"
                    >
                      ✓ Done — record it
                    </button>
                    <button
                      onClick={() => resolveMergeReview(i, 'add')}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      <Plus size={11} /> Still a to-do — add it
                    </button>
                    <button
                      onClick={() => resolveMergeReview(i, 'skip')}
                      className="rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      Ignore
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    looks similar to: <span className="font-medium">“{entry.matchText}”</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    <button
                      onClick={() => resolveMergeReview(i, 'skip')}
                      className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      Same task — skip it
                    </button>
                    <button
                      onClick={() => resolveMergeReview(i, 'add')}
                      className="flex items-center gap-1 rounded-lg bg-brand-600 px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-brand-500"
                    >
                      <Plus size={11} /> Different — add it
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Why the AI wasn't used for the last Organize, verbatim (CLAUDE.md §2).
          9 Oct 2026: this notice was only rendered on the dump screen, so a
          successful local-engine fallback was silent on the board. */}
      {providerNotice && (
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-950/20 p-3.5">
          <p className="text-xs text-amber-700 dark:text-amber-400">{providerNotice}</p>
          <button
            onClick={clearProviderNotice}
            aria-label="Dismiss"
            className="shrink-0 rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Smart-merge summary (after importing an updated list) */}
      {importNotice && (
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-brand-200 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/10 p-3.5">
          <p className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
            <Sparkles size={15} className="mt-0.5 shrink-0 text-brand-600 dark:text-brand-400" />
            <span>{importNotice}</span>
          </p>
          <button
            onClick={clearImportNotice}
            aria-label="Dismiss"
            className="shrink-0 rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* One-time gentle pointer: your data, your controls (dismissible) */}
      {!privacyNudgeSeen && allTasks.length > 0 && (
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-950/20 p-3.5">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={17} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                Your data is yours — no trackers, no ads, no AI training on your tasks.
              </h4>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
                Export everything, delete your cloud data, or erase this device —
                anytime, yourself, in Settings.
              </p>
              <button
                onClick={() => {
                  markPrivacyNudgeSeen();
                  openSettings();
                }}
                className="mt-2 rounded-lg border border-emerald-300 dark:border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
              >
                See my privacy controls
              </button>
            </div>
          </div>
          <button
            onClick={markPrivacyNudgeSeen}
            aria-label="Dismiss"
            className="shrink-0 rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Fatigue alert banner — collapsible (founder, 24 Aug: the full card
          costs too much board space on a phone). Collapsed it keeps the
          COUNT and the action; only the explanation is hidden, so the signal
          is never silently lost. Sticky across sessions, device-local. */}
      {fatigued.length > 0 &&
        (fatigueCollapsed ? (
          /* Collapsed: ONE row — count, action, expand. Everything the
             banner is FOR, none of the height. */
          <div className="flex items-center gap-2 rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-gradient-to-r from-amber-50 dark:from-amber-950/20 to-brand-50 dark:to-brand-900/10 px-3.5 py-2">
            <Clock size={15} className="shrink-0 text-amber-600 dark:text-amber-400" />
            <h4 className="min-w-0 flex-1 truncate text-xs font-bold text-slate-900 dark:text-white">
              {fatigued.length === 1 ? 'A task keeps' : `${fatigued.length} tasks keep`} coming back
            </h4>
            <button
              onClick={openRollover}
              className="shrink-0 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold text-amber-600 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20"
            >
              Open Wrap-Up
            </button>
            <button
              onClick={toggleFatigueCollapsed}
              aria-label="Show why this matters"
              aria-expanded={false}
              className="shrink-0 rounded-lg p-1 text-amber-600 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20"
            >
              <ChevronDown size={14} />
            </button>
          </div>
        ) : (
          <div className="space-y-2 rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-gradient-to-r from-amber-50 dark:from-amber-950/20 to-brand-50 dark:to-brand-900/10 p-3.5 lg:flex lg:items-center lg:justify-between lg:space-y-0">
            <div className="flex items-start gap-2">
              <Clock size={17} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                  {fatigued.length === 1 ? 'A task keeps' : `${fatigued.length} tasks keep`} coming back
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  That usually means {fatigued.length === 1 ? "it's" : "they're"} too big — not
                  that you failed. The wrap-up helps you shrink, hand off, or let go.
                </p>
              </div>
              <button
                onClick={toggleFatigueCollapsed}
                aria-label="Collapse this panel"
                aria-expanded={true}
                className="ml-auto shrink-0 rounded-lg p-1 text-amber-600 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20 lg:hidden"
              >
                <ChevronUp size={14} />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={openRollover}
                className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold text-amber-600 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20"
              >
                Open Wrap-Up
              </button>
              <button
                onClick={toggleFatigueCollapsed}
                aria-label="Collapse this panel"
                aria-expanded={true}
                className="hidden shrink-0 rounded-lg p-1.5 text-amber-600 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20 lg:inline-flex"
              >
                <ChevronUp size={14} />
              </button>
            </div>
          </div>
        ))}

      {/* Week controls + quick add (side by side on desktop) */}
      <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
        <div className="flex items-center justify-between rounded-2xl border border-brand-200 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/10 p-4">
          <div>
            <span className="text-[11px] font-extrabold uppercase tracking-widest text-brand-600 dark:text-brand-400">
              Active Operating Week
            </span>
            <h3 className="text-xs font-bold text-slate-900 dark:text-white">{weekLabel()}</h3>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {formatFullDate()}
            </p>
          </div>
          <button
            onClick={openRollover}
            className="flex items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-[11px] font-bold text-white shadow-md shadow-brand-600/10 transition-all hover:bg-brand-500 active:scale-95"
          >
            <RefreshCw size={13} />
            Start New Week
          </button>
        </div>

        {/* Quick add. STACKS ON PHONES (founder, 2 Sept): all four controls on
            one 390px row left the input about 130px wide, so its own
            placeholder truncated to "Quick task ir" — the field looked broken
            before a single character was typed. On <640px the input takes a
            full row and the mic/zone/add controls sit beneath it; from `sm:`
            up the original single row is restored unchanged, because the
            desktop layout was never the problem. The mic is conditional
            (`voice.supported`), so both arrangements must survive its
            absence — they do: it is a sibling in the same flex row either
            way. */}
        <div className="space-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={quickText}
              onChange={(e) => setQuickText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitQuickAdd()}
              autoComplete="off"
              name="zonein-quick-task"
              placeholder="Quick task input…"
              className="w-full min-w-0 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-3 py-2.5 text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-brand-500 focus:outline-none sm:flex-1"
            />
            <div className="flex items-center gap-2">
            {/* Scan from the board (10 Oct 2026): three people in one evening
                couldn't find scanning — it lived only at the bottom of the
                board, under "Add to Matrix & Re-Prioritize". One tap now
                opens the brain dump (merge) with the scanner already open. */}
            <button
              onClick={openScanFromBoard}
              aria-label="Scan a paper list"
              title="Scan a paper list"
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-2.5 text-brand-600 dark:text-brand-400 transition-all hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95"
            >
              <Camera size={16} />
            </button>
            {voice.supported && (
              <button
                onClick={() => voice.toggle(quickText)}
                aria-label={voice.listening ? 'Stop dictation' : 'Dictate task'}
                className={`rounded-xl border p-2.5 transition-all active:scale-95 ${
                  voice.listening
                    ? 'animate-pulse border-red-500 bg-red-600 text-white'
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-brand-600 dark:text-brand-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                {voice.listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            )}
            <select
              value={quickZone}
              onChange={(e) => setQuickZone(e.target.value as ZoneId)}
              aria-label="Zone"
              className="min-w-0 flex-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2 py-2.5 text-xs text-slate-600 dark:text-slate-300 focus:outline-none sm:flex-none"
            >
              {ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.emoji} {z.id.replace('zone', 'Zone ')}
                </option>
              ))}
            </select>
            <button
              onClick={submitQuickAdd}
              aria-label="Add task"
              className="shrink-0 rounded-xl bg-brand-600 p-2.5 text-white transition-all hover:bg-brand-500 active:scale-95"
            >
              <Plus size={16} />
            </button>
            </div>
          </div>
          {voice.listening && (
            <p role="status" className="text-[11px] text-slate-500 dark:text-slate-400">🎤 {QUICK_DICTATION_HINT}</p>
          )}
          {voice.notice && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">🎤 {voice.notice}</p>
          )}
        </div>
      </div>

      {allTasks.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 text-center">
          <p className="text-sm text-slate-500">
            No tasks yet — quick-add above, or run a full brain dump below.
          </p>
        </div>
      )}

      {/* Focus toggle: one calm view for overwhelmed moments */}
      {allTasks.length > 0 && (
        <button
          onClick={toggleFocusMode}
          aria-pressed={focusMode}
          className={`flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-bold transition-colors ${
            focusMode
              ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
              : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Crosshair size={14} />
          {focusMode ? 'Show the full matrix' : 'Feeling overwhelmed? Focus on what matters'}
        </button>
      )}

      {/* Focus view: Zone 1 + the top of Zone 2, nothing else on screen */}
      {focusMode && allTasks.length > 0 && (
        <div className="space-y-4">
          {(
            [
              { zone: ZONE_BY_ID.zone1, items: byZone.zone1.filter((t) => !t.done) },
              { zone: ZONE_BY_ID.zone2, items: byZone.zone2.filter((t) => !t.done).slice(0, 3) },
            ] as { zone: (typeof ZONES)[number]; items: Task[] }[]
          ).map(({ zone, items }) => (
            <div key={zone.id} className={`rounded-2xl border bg-slate-50 dark:bg-slate-950 shadow-lg ${zone.color.border}`}>
              <div className={`flex items-center gap-2 p-4 ${zone.color.headerBg}`}>
                <ZoneIcon zone={zone.id} size={15} className={zone.color.text} />
                <span className={`text-xs font-bold uppercase tracking-wider ${zone.color.text}`}>
                  {zone.title}
                </span>
              </div>
              <div className="space-y-2 p-3">
                {items.length === 0 && (
                  <p className="px-1 pb-1 text-xs text-slate-500">
                    Nothing here right now — that's a good thing. 🎉
                  </p>
                )}
                {items.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            </div>
          ))}
          <p className="text-center text-[11px] text-slate-500">
            Everything else is safe in the full matrix — one thing at a time.
          </p>
        </div>
      )}

      {/* Today lens (V2 §1): scope switcher — same matrix, just fewer cards */}
      {allTasks.length > 0 && !focusMode && (
        <div className="space-y-2">
          {/* 15 Oct 2026 (founder, desktop): Sort sat at the far right of a
              wide row as faint grey text and was not seen at all. It now sits
              right beside All / Today, with an icon and a border that read as
              a control. */}
          <div className="flex items-center gap-2">
            {/* shrink-0 (14 Oct 2026, Android, large system text): the
                row ran out of width and the group shrank until "Today"
                read "To". The scope buttons never shrink; the sort select
                gives way instead. */}
            <div
              role="group"
              aria-label="Scope"
              className="inline-flex shrink-0 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
            >
              <button
                onClick={() => setTodayLens(false)}
                aria-pressed={!todayLensOn}
                className={`whitespace-nowrap px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
                  !todayLensOn
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setTodayLens(true)}
                aria-pressed={todayLensOn}
                className={`flex items-center gap-1 whitespace-nowrap px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
                  todayLensOn
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Sunrise size={12} />
                Today
              </button>
            </div>
            {/* Sort — shown to EVERYONE with tasks (14 Oct 2026). It used to
                appear only when the board had tags, so a board without tags
                (the iPhone tester's) had no Sort at all, though Priority and
                Date sorts need no tags. "Project / Role" is offered only when
                there are tags to sort by; a saved 'tag' mode on a tagless
                board sorts AND shows as Priority (effectiveSort). */}
            <label className="flex min-w-0 max-w-[60%] items-center gap-1 rounded-xl border border-brand-200 dark:border-brand-500/30 bg-white dark:bg-slate-900 pl-2 focus-within:border-brand-500">
              {/* Not ArrowUpDown: that icon is a task's move button (TaskCard) —
                  two controls must not look alike (15 Oct 2026). */}
              <ArrowDownWideNarrow size={12} className="shrink-0 text-brand-600 dark:text-brand-400" aria-hidden="true" />
              <select
                value={effectiveSort}
                onChange={(e) => setSortMode(e.target.value as 'priority' | 'date' | 'tag')}
                aria-label="Sort tasks"
                title="Change the order of tasks inside each zone"
                className="min-w-0 flex-1 cursor-pointer truncate bg-transparent py-1.5 pr-1 text-[11px] font-bold text-brand-700 dark:text-brand-300 focus:outline-none"
              >
                <option value="priority">Sort: Priority</option>
                <option value="date">Sort: Date</option>
                {hasTags && <option value="tag">Sort: Project / Role</option>}
              </select>
            </label>
            {/* What do All / Today / Sort do? One tap to the guide section
                (15 Oct 2026). publicUrl: absolute on native, so iOS opens it
                (CLAUDE.md §9, new-window links). */}
            <a
              href={publicUrl('/guide.html#today-sort')}
              target="_blank"
              rel="noreferrer"
              aria-label="How All, Today and Sort work"
              title="How All, Today and Sort work"
              className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:text-brand-600 dark:hover:text-brand-400"
            >
              <HelpCircle size={14} />
            </a>
          </div>
          {/* Law 3 (no silent state): the lens always says what it hides — on
              its own line (14 Oct 2026): squeezed into the row above it was
              truncated to a second "To…" beside the Today button. */}
          {todayLensOn && lensHidden > 0 && (
            <button
              onClick={() => setTodayLens(false)}
              className="w-full rounded-full border border-brand-300 dark:border-brand-500/30 bg-brand-500/10 px-3 py-1 text-left text-[11px] font-bold text-brand-600 dark:text-brand-400 transition-colors hover:bg-brand-500/20"
            >
              Showing today only · {lensHidden} hidden — show all
            </button>
          )}
        </div>
      )}

      {todayLensOn && !focusMode && allTasks.length > 0 && lensVisibleOpen === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-emerald-300 dark:border-emerald-500/30 p-6 text-center">
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            Nothing due today. Your future self says thanks. 🌅
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            Everything else is waiting safely under “All”.
          </p>
        </div>
      )}

      {/* Tag filter + sort bar — Jill's role view, Jack's project view.
          One-row rail forever (V2 §4d, register #27): "All" and the active
          tag stay pinned; the rest scroll horizontally, most-used first. */}
      {Object.keys(tags).length > 0 && !focusMode && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setActiveTag(undefined)}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                !activeTagId
                  ? 'border-brand-400 bg-brand-600/10 text-brand-600 dark:text-brand-400'
                  : 'border-slate-200 dark:border-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              All
            </button>
            {activeTag && (
              <button
                onClick={() => setActiveTag(undefined)}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-bold"
                style={{
                  color: activeTag.color,
                  borderColor: activeTag.color,
                  backgroundColor: `${activeTag.color}22`,
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: activeTag.color }} />
                {activeTag.name}
              </button>
            )}
            {/* Custom slider (Chad, 12 Aug ×2): native scrollbars are invisible
                overlays on phones, so we draw our own — same slim draggable
                slider on every platform. Swipe still works; the native
                scrollbar is hidden. */}
            <div className="min-w-0 flex-1">
              <div
                ref={railRef}
                onScroll={updateRail}
                className="flex items-center gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {/* ALPHABETICAL (founder decision, 12 Aug, register #27
                    amendment): most-used-first reshuffled as usage changed —
                    disorienting, especially for ADHD users. A stable A–Z
                    order means a chip is always where it was yesterday. */}
                {Object.values(tags)
                  .filter((tag) => tag.id !== activeTagId)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => setActiveTag(tag.id)}
                      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-bold opacity-60 transition-all hover:opacity-100"
                      style={{
                        color: tag.color,
                        borderColor: `${tag.color}55`,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </button>
                  ))}
              </div>
              {rail.visible && (
                <div
                  role="scrollbar"
                  aria-controls="tag-rail"
                  aria-valuenow={Math.round(rail.left * 100)}
                  onPointerDown={dragRail}
                  className="group relative mt-1 h-4 cursor-pointer touch-none"
                >
                  <span className="absolute inset-x-0 top-1 block h-2 rounded-full bg-slate-200 dark:bg-slate-800" />
                  <span
                    className="absolute top-0.5 block h-3 rounded-full bg-brand-500/50 transition-colors group-hover:bg-brand-500 group-active:bg-brand-500"
                    style={{ left: `${rail.left * 100}%`, width: `${Math.max(rail.thumb * 100, 12)}%` }}
                  />
                </div>
              )}
            </div>
            {/* The sort select used to sit here. Moved up beside All/Today
                (2 Sept) so this row belongs entirely to the tag rail and its
                slider. Do not put a fixed-width control back on this line —
                every pixel here comes straight out of the rail. */}
          </div>

          {/* Coverage strip: which categories are covered, where to put time */}
          {activeTag && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-2xl border p-3"
              style={{ borderColor: `${activeTag.color}55`, backgroundColor: `${activeTag.color}0d` }}
            >
              <span className="text-xs font-bold" style={{ color: activeTag.color }}>
                {activeTag.name}
              </span>
              {(() => {
                const s = tagStats(allTasks, activeTag.id);
                return (
                  <>
                    <span className="text-[11px] font-medium text-slate-600 dark:text-slate-400">
                      {s.done} done · {s.open} in play · {Math.round(s.progress * 100)}%
                    </span>
                    <span className="h-1.5 min-w-24 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <span
                        className="block h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.round(s.progress * 100)}%`, backgroundColor: activeTag.color }}
                      />
                    </span>
                  </>
                );
              })()}
              <button
                onClick={openTagReport}
                className="rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: activeTag.color }}
              >
                Status report…
              </button>
              {/* Editable report preview — review & adjust before sending. */}
              {reportDraft != null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-label="Status report preview">
                  <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 p-4 shadow-2xl">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Status report — review &amp; edit
                      </h4>
                      <button
                        onClick={() => setReportDraft(null)}
                        className="rounded-lg p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        aria-label="Close"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <textarea
                      value={reportDraft}
                      onChange={(e) => setReportDraft(e.target.value)}
                      className="mt-2 h-72 w-full resize-y rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/50 p-3 font-mono text-xs leading-relaxed text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      spellCheck={true}
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      Edit anything before sending — changes here never touch your tasks.
                    </p>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => void copyReport()}
                        className="rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        {reportCopied ? 'Copied ✓' : 'Copy'}
                      </button>
                      <button
                        onClick={() => void shareReport()}
                        className="rounded-lg px-4 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: activeTag.color }}
                      >
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Tag management — rename (merges into an existing name,
                  which is how OCR-duplicate tags get healed) and delete. */}
              {tagRenameOpen ? (
                <span className="flex items-center gap-1">
                  <input
                    type="text"
                    value={tagNameDraft}
                    onChange={(e) => setTagNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagNameDraft.trim()) {
                        renameTag(activeTag.id, tagNameDraft);
                        setTagRenameOpen(false);
                      }
                      if (e.key === 'Escape') setTagRenameOpen(false);
                    }}
                    autoFocus
                    aria-label="Rename tag"
                    className="w-36 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] text-slate-900 dark:text-white focus:border-brand-500 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      if (tagNameDraft.trim()) renameTag(activeTag.id, tagNameDraft);
                      setTagRenameOpen(false);
                    }}
                    aria-label="Save tag name"
                    className="rounded-md bg-brand-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-brand-500"
                  >
                    ✓
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => {
                    setTagNameDraft(activeTag.name);
                    setTagRenameOpen(true);
                    setTagDeleteArmed(false);
                  }}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  title="Rename this tag (renaming to an existing tag's name merges them)"
                >
                  Rename
                </button>
              )}
              {/* Color picker: distinct-at-a-glance is the whole point of
                  tag colors — one tap reassigns. */}
              <span className="flex items-center gap-1">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setTagColor(activeTag.id, c)}
                    aria-label={`Set tag color ${c}`}
                    title="Set tag colour"
                    className={`h-4 w-4 rounded-full transition-transform hover:scale-125 ${
                      activeTag.color === c ? 'ring-2 ring-slate-500 ring-offset-1 dark:ring-offset-slate-900' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </span>
              <button
                onClick={() => {
                  if (!tagDeleteArmed) {
                    setTagDeleteArmed(true);
                    setTimeout(() => setTagDeleteArmed(false), 4000);
                    return;
                  }
                  deleteTag(activeTag.id);
                  setTagDeleteArmed(false);
                }}
                className={`rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${
                  tagDeleteArmed
                    ? 'border-red-400 bg-red-500/10 text-red-600 dark:text-red-400'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:text-red-500'
                }`}
                title="Tasks keep their other tags — only the label is removed"
              >
                {tagDeleteArmed ? 'Tap again to delete' : 'Delete tag'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Zones: accordion on mobile, 2×2 quadrant grid on desktop.
          Spotlight (V2 §5): one zone full-screen, siblings as thin rails. */}
      {!focusMode && (
      <div className={spotlightZone ? 'space-y-2' : 'space-y-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0'}>
        {ZONES.map((zone) => {
          const zoneTasks = byZone[zone.id];
          const spotlighted = spotlightZone === zone.id;
          const open = spotlighted || (!spotlightZone && (isDesktop || openZone === zone.id));
          const remaining = zoneTasks.filter((t) => !t.done).length;
          const progress = zoneProgress(zoneTasks);
          // Sibling of the spotlighted zone: a thin colored rail, one tap
          // to move the spotlight there (and the ✕ on the header returns).
          if (spotlightZone && !spotlighted) {
            return (
              <button
                key={zone.id}
                onClick={() => setSpotlightZone(zone.id)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-2 ${zone.color.border} ${zone.color.headerBg}`}
              >
                <span className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider ${zone.color.text}`}>
                  <ZoneIcon zone={zone.id} size={13} className={zone.color.text} />
                  {zone.title}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${zone.color.badgeBg} ${zone.color.badgeText}`}>
                  {remaining}
                </span>
              </button>
            );
          }
          return (
            <div
              key={zone.id}
              className={`overflow-hidden rounded-2xl border bg-slate-50 dark:bg-slate-950 shadow-lg lg:flex lg:min-h-[16rem] lg:flex-col ${zone.color.border}`}
            >
              <button
                onClick={() => !isDesktop && !spotlighted && setOpenZone(open ? null : zone.id)}
                className={`relative flex w-full items-center justify-between p-4 ${zone.color.headerBg} ${
                  isDesktop || spotlighted ? 'cursor-default' : ''
                }`}
                aria-expanded={open}
              >
                {/* Zone progress: the week visibly filling up */}
                {zoneTasks.length > 0 && (
                  <span
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-[3px] bg-slate-200/60 dark:bg-slate-800/60"
                  >
                    <span
                      className="block h-full bg-emerald-500 transition-all duration-500 dark:bg-emerald-400"
                      style={{ width: `${Math.round(progress * 100)}%` }}
                    />
                  </span>
                )}
                <span
                  className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${zone.color.text}`}
                >
                  <ZoneIcon zone={zone.id} size={15} className={zone.color.text} />
                  <span>{zone.title}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-extrabold ${zone.color.badgeBg} ${zone.color.badgeText}`}
                  >
                    {remaining}
                  </span>
                  {/* Spotlight glyph (V2 §5) — span, not button: the header
                      itself is a button and buttons can't nest. */}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={spotlighted ? `Exit ${zone.title} spotlight` : `Spotlight ${zone.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpotlightZone(spotlighted ? null : zone.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setSpotlightZone(spotlighted ? null : zone.id);
                      }
                    }}
                    className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-200/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white"
                  >
                    {spotlighted ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                  </span>
                  {!spotlighted && (
                    <ChevronDown
                      size={15}
                      className={`text-slate-500 transition-transform lg:hidden ${
                        open ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={isDesktop ? false : { height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="lg:flex-1"
                  >
                    <div className="space-y-2 p-3">
                      <p className="px-1 text-xs italic text-slate-500">{zone.subtitle}</p>
                      {zoneTasks.length === 0 && (
                        <p className="px-1 pb-2 text-xs text-slate-500">
                          Nothing in this zone. 🎉
                        </p>
                      )}
                      {zoneTasks.map((task) => (
                        <TaskCard key={task.id} task={task} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      )}

      <button
        onClick={() => {
          setImportStrategy('merge');
          setScreen('dump');
        }}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
      >
        <BrainCircuit size={15} className="text-brand-600 dark:text-brand-400" />
        Brain Dump: Add to Matrix & Re-Prioritize (AI)
      </button>

      {allTasks.length > 0 && <PrintButton />}

      <ShareButton />

      {/* The Bin, surfaced on the board (Chad, 29 Jul): a recovery feature
          hidden in Settings isn't a safety net. Appears whenever it holds
          something; the Settings card remains as a second door. */}
      {visibleBin(bin).length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            onClick={() => setBinOpen((o) => !o)}
            className="flex w-full items-center justify-center gap-2 py-3.5 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
            aria-expanded={binOpen}
          >
            <Trash2 size={15} className="text-brand-600 dark:text-brand-400" />
            Bin ({visibleBin(bin).length}) — restore deleted tasks
            {binOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {binOpen && (
            <div className="border-t border-slate-200 dark:border-slate-800 p-3 space-y-1.5">
              {visibleBin(bin).map((e) => (
                <div
                  key={e.task.id}
                  className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 dark:bg-slate-950/50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                      {e.task.text}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {binDaysLeft(e, Date.now())} day
                      {binDaysLeft(e, Date.now()) === 1 ? '' : 's'} left
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => {
                        setPurgeArmedId(null);
                        restoreFromBin(e.task.id);
                      }}
                      className="rounded-lg border border-brand-300 dark:border-brand-500/40 px-3 py-1.5 text-[11px] font-semibold text-brand-600 dark:text-brand-400 transition-colors hover:bg-brand-50 dark:hover:bg-brand-950/30"
                    >
                      Restore
                    </button>
                    {purgeArmedId === e.task.id ? (
                      <button
                        onClick={() => {
                          purgeFromBin(e.task.id);
                          setPurgeArmedId(null);
                        }}
                        className="rounded-lg border border-red-400 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-bold text-red-600 dark:text-red-400"
                      >
                        Sure?
                      </button>
                    ) : (
                      <button
                        onClick={() => setPurgeArmedId(e.task.id)}
                        aria-label={`Delete "${e.task.text}" forever`}
                        title="Delete forever"
                        className="rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-slate-400 transition-colors hover:border-red-300 hover:text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <p className="pt-1 text-[10px] text-slate-400">
                Deleted tasks are kept for 7 days, then removed for good — or
                tap the bin icon to remove one now.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
